/**
 * Zalchos Platform — Role Definitions
 *
 * Four isolated frontend applications share this single backend (Supabase).
 * Each role maps to a distinct app with isolated UI, navigation, and access rules.
 */

export type AppRole = 'customer' | 'driver' | 'vendor' | 'admin';

export interface RoleConfig {
  role: AppRole;
  appName: string;
  defaultRoute: string;
  loginRoute: string;
  description: string;
}

export const ROLE_CONFIGS: Record<AppRole, RoleConfig> = {
  customer: {
    role: 'customer',
    appName: 'Zalchos',
    defaultRoute: '/customer-home',
    loginRoute: '/customer-login',
    description: 'Customer Mobile App',
  },
  driver: {
    role: 'driver',
    appName: 'Zalchos Driver',
    defaultRoute: '/driver-home',
    loginRoute: '/driver-login',
    description: 'Driver Mobile App',
  },
  vendor: {
    role: 'vendor',
    appName: 'Zalchos Partner',
    defaultRoute: '/apps/vendor',
    loginRoute: '/apps/vendor/login',
    description: 'Vendor Portal (Web / PWA)',
  },
  admin: {
    role: 'admin',
    appName: 'Zalchos Admin',
    defaultRoute: '/apps/admin',
    loginRoute: '/apps/admin/login',
    description: 'Admin Dashboard (Web)',
  },
};

/**
 * Determine which app role a given pathname belongs to.
 * Used for route-level isolation.
 */
export function getRoleFromPath(pathname: string): AppRole | null {
  if (pathname.startsWith('/vendor')) return 'vendor';
  if (pathname.startsWith('/admin') || isAdminPath(pathname)) return 'admin';
  if (pathname.startsWith('/driver')) return 'driver';
  if (pathname.startsWith('/customer')) return 'customer';
  return null;
}

const ADMIN_PATHS = [
  '/marketplace-payment-rules-engine',
  '/driver-availability-tracking-center',
  '/fraud-investigation-center',
  '/customer-support-ticket-management',
  '/risk-scoring-configuration',
  '/payment-processing-audit-trail',
  '/stripe-connect-onboarding-dashboard',
  '/province-payment-routing',
  '/dispatch-analytics-and-reporting-dashboard',
  '/ai-support-analytics-dashboard',
  '/configuration-controls',
  '/user-management',
  '/order-management',
  '/agco-payment-processing-dashboard',
  '/revenue-and-payments-dashboard',
  '/stripe-webhook-management-center',
  '/payment-configuration-management',
  '/financial-reconciliation-center',
  '/delivery-zone-management',
  '/fraud-detection-dashboard',
  '/dynamic-delivery-pricing-dashboard',
  '/order-support-management-dashboard',
  '/order-dispute-resolution-center',
  '/promotions-management',
  '/ai-customer-support-dashboard',
  '/compliance-audit-dashboard',
  '/transaction-ledger-dashboard',
  '/driver-dispatch-management-dashboard',
  '/order-audit-trail-dashboard',
  '/promotions-analytics-dashboard',
  '/driver-earnings-analytics',
];

function isAdminPath(pathname: string): boolean {
  return ADMIN_PATHS.some((p) => pathname.startsWith(p));
}
